/**
 * AI Chat API 接口定义
 * 数字张雪峰AI高考志愿填报咨询系统
 */

import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 当前学生ID（用于API调用）
let currentStudentId: number | null = null;

export function setCurrentStudentId(studentId: number | null) {
  currentStudentId = studentId;
}

export function getCurrentStudentId(): number | null {
  return currentStudentId;
}

// ==================== 类型定义 ====================

export interface StudentInfo {
  name?: string;
  school?: string;
  grade?: string;
  examType?: 'senior' | 'junior';
  stream?: "physics" | "history"; // 物理类/历史类
  totalScore?: number;
  chineseScore?: number;
  mathScore?: number;
  englishScore?: number;
  physicsScore?: number;
  chemistryScore?: number;
  geographyScore?: number;
  historyScore?: number; // 历史类选科成绩
  biologyScore?: number; // 生物成绩
  politicsScore?: number; // 政治成绩
  targetCities?: string[];
  preferredMajors?: string[];
  schoolTierPreferences?: string[];
  targetUniversityLevel?: "985" | "211" | "双一流" | "普通本科" | "专科" | "一双外" | "普高" | "职高" | "示范高中";
  provinceRanking?: number;
}

export interface ChatSession {
  id: number;
  title: string;
  studentInfo: StudentInfo;
  completionRate: number;
  createdAt: string;
  updatedAt: string;
}

export interface ChatMessage {
  id: number;
  sessionId: number;
  role: 'user' | 'assistant';
  content: string;
  sources?: Source[];
  suggestedQuestions?: string[];
  createdAt: string;
}

export interface Source {
  type: 'video_segment' | 'knowledge_entry' | 'qa_pair';
  id: number;
  title: string;
  content: string;
  videoTitle?: string;
  timestamp?: number;
  confidence?: number;
  // 视频片段相关字段
  segmentText?: string;
  startTime?: string;
  endTime?: string;
}

export interface Video {
  id: number;
  title: string;
  duration: number;
  thumbnail?: string;
  createdAt: string;
}

export interface VideoSegment {
  id: number;
  videoId: number;
  startTime: number;
  endTime: number;
  text: string;
  summary?: string;
  tags?: string[];
}

export interface KnowledgeEntry {
  id: number;
  title: string;
  content: string;
  category: string;
  tags: string[];
  createdAt: string;
}

export interface KnowledgeStats {
  totalVideos: number;
  totalSegments: number;
  totalKnowledge: number;
}

export interface SendMessageRequest {
  sessionId?: number;
  message: string;
  studentInfo?: StudentInfo;
}

export interface SendMessageResponse {
  message: ChatMessage;
  session: ChatSession;
  sources: Source[];
  suggestedQuestions: string[];
}

export interface SearchKnowledgeRequest {
  query: string;
  topK?: number;
  filters?: {
    category?: string;
    sourceType?: string;
  };
}

// ==================== 对话API ====================

/**
 * 创建新会话
 */
export async function createSession(studentInfo?: StudentInfo): Promise<ChatSession> {
  if (!currentStudentId) {
    throw new Error('No student selected');
  }
  const response = await api.post<{ success: boolean; session_id: number; message: string }>('/chat/sessions', { 
    student_id: currentStudentId,
    title: '新建对话'
  });
  // 转换后端返回的数据格式到前端格式
  return {
    id: response.data.session_id,
    title: '新建对话',
    studentInfo: studentInfo || {},
    completionRate: 0,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
}

/**
 * 获取会话列表
 */
export async function getSessions(): Promise<ChatSession[]> {
  if (!currentStudentId) {
    throw new Error('No student selected');
  }
  const response = await api.get<{ success: boolean; sessions: any[]; count: number }>(`/chat/sessions?student_id=${currentStudentId}`);
  // 转换后端返回的数据格式到前端格式（使用后端返回的学生信息）
  return response.data.sessions.map((session: any) => ({
    id: session.session_id || session.id,
    title: session.title,
    studentInfo: session.studentInfo || {},
    completionRate: session.completionRate || 0,
    createdAt: session.created_at,
    updatedAt: session.updated_at,
  }));
}

/**
 * 获取单个会话详情
 */
export async function getSession(sessionId: number): Promise<ChatSession> {
  const response = await api.get<ChatSession>(`/chat/sessions/${sessionId}`);
  return response.data;
}

/**
 * 删除会话
 */
export async function deleteSession(sessionId: number): Promise<void> {
  await api.delete(`/chat/sessions/${sessionId}`);
}

/**
 * 发送消息
 */
export async function sendMessage(request: SendMessageRequest): Promise<SendMessageResponse> {
  const response = await api.post<{
    success: boolean;
    sessionId: number;
    aiResponse: string;
    sources: Source[];
    followUps: string[];
    messages: any[];
    intent: string;
    completionRate: number;
    studentInfo?: StudentInfo;
  }>('/chat/message', {
    session_id: request.sessionId,
    message: request.message
  });
  
  // 转换后端返回的数据格式到前端格式（使用后端返回的更新后的学生信息）
  return {
    message: {
      id: Date.now(),
      sessionId: response.data.sessionId,
      role: 'assistant',
      content: response.data.aiResponse,
      sources: response.data.sources,
      suggestedQuestions: response.data.followUps,
      createdAt: new Date().toISOString(),
    },
    session: {
      id: response.data.sessionId,
      title: '对话',
      studentInfo: response.data.studentInfo || request.studentInfo || {},
      completionRate: response.data.completionRate || 0,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    },
    sources: response.data.sources || [],
    suggestedQuestions: response.data.followUps || [],
  };
}

/**
 * 获取会话消息列表
 */
export async function getMessages(sessionId: number): Promise<ChatMessage[]> {
  const response = await api.get<{ success: boolean; messages: any[] }>(`/chat/sessions/${sessionId}/messages`);
  // 转换后端返回的数据格式到前端格式
  return (response.data.messages || []).map(msg => ({
    id: msg.id,
    sessionId: msg.session_id,
    role: msg.role,
    content: msg.content,
    sources: msg.sources ? JSON.parse(msg.sources) : undefined,
    suggestedQuestions: msg.suggested_questions ? JSON.parse(msg.suggested_questions) : undefined,
    createdAt: msg.created_at,
  }));
}

// ==================== 知识库API ====================

/**
 * 搜索知识库（RAG）
 */
export async function searchKnowledge(request: SearchKnowledgeRequest): Promise<{
  results: (KnowledgeEntry | VideoSegment)[];
  sources: Source[];
}> {
  const response = await api.post('/knowledge/search', request);
  return response.data;
}

/**
 * 获取知识库统计信息
 */
export async function getKnowledgeStats(): Promise<KnowledgeStats> {
  const response = await api.get<KnowledgeStats>('/knowledge/stats');
  return response.data;
}

/**
 * 获取知识库列表
 */
export async function getKnowledge(params?: {
  page?: number;
  limit?: number;
  category?: string;
}): Promise<{ entries: KnowledgeEntry[]; total: number }> {
  const response = await api.get('/knowledge', { params });
  return response.data;
}

/**
 * 创建知识条目
 */
export async function createKnowledge(data: {
  title: string;
  content: string;
  category: string;
  tags?: string[];
}): Promise<KnowledgeEntry> {
  const response = await api.post<KnowledgeEntry>('/knowledge', data);
  return response.data;
}

// ==================== 视频API ====================

/**
 * 获取视频列表
 */
export async function getVideos(params?: {
  page?: number;
  limit?: number;
}): Promise<{ videos: Video[]; total: number }> {
  const response = await api.get('/videos', { params });
  return response.data;
}

/**
 * 获取视频详情
 */
export async function getVideo(videoId: number): Promise<Video & { segments: VideoSegment[] }> {
  const response = await api.get(`/videos/${videoId}`);
  return response.data;
}

/**
 * 获取视频片段列表
 */
export async function getVideoSegments(videoId: number): Promise<VideoSegment[]> {
  const response = await api.get(`/videos/${videoId}/segments`);
  return response.data;
}

// ==================== Embedding API ====================

/**
 * 创建向量嵌入
 */
export async function createEmbedding(text: string): Promise<{ id: number; vector: number[] }> {
  const response = await api.post('/embeddings/create', { text });
  return response.data;
}

/**
 * 批量创建向量嵌入
 */
export async function createBatchEmbeddings(texts: string[]): Promise<{ embeddings: { id: number; vector: number[] }[] }> {
  const response = await api.post('/embeddings/batch', { texts });
  return response.data;
}

// ==================== 工具函数 ====================

/**
 * 格式化时长（秒 -> MM:SS）
 */
export function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
}

/**
 * 格式化时间戳（秒 -> MM:SS）
 */
export function formatTimestamp(seconds: number): string {
  return formatDuration(seconds);
}

/**
 * 格式化日期
 */
export function formatDate(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return '刚刚';
  if (diffMins < 60) return `${diffMins}分钟前`;
  if (diffHours < 24) return `${diffHours}小时前`;
  if (diffDays < 7) return `${diffDays}天前`;

  return date.toLocaleDateString('zh-CN');
}

/**
 * 计算信息完整度
 */
export function calculateCompletionRate(studentInfo: StudentInfo): number {
  // 必需字段（60%）
  const requiredFields = ['name', 'school', 'grade', 'stream', 'totalScore'];
  const requiredCompleted = requiredFields.filter(field => studentInfo[field as keyof StudentInfo] !== undefined).length;
  const requiredScore = (requiredCompleted / requiredFields.length) * 60;

  // 可选字段（20%）
  const optionalFields = ['targetCities', 'preferredMajors', 'targetUniversityLevel'];
  const optionalCompleted = optionalFields.filter(field => {
    const value = studentInfo[field as keyof StudentInfo];
    if (Array.isArray(value)) return value.length > 0;
    return value !== undefined;
  }).length;
  const optionalScore = (optionalCompleted / optionalFields.length) * 20;

  // 交互深度（20%）- 需要从session获取，这里暂时设为0
  const interactionScore = 0;

  return Math.round(requiredScore + optionalScore + interactionScore);
}

export default api;
